/** Bulk "Multi-CDN" — compress content, ignore query string, clone a routing
 * strategy, plus per-platform CDN control (on/off) and Custom CDN CNAME (BYO edge
 * domain) across the selected domains. */
import { useMemo, useState } from 'react';
import {
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import {
  useWafDomains,
  useWafQueryPlatforms,
  useBulkSetPlatformEnabled,
  useBulkSetPlatformDomain,
} from '@/api/waf';
import { useToast } from '@vnetwork/ui/feedback';
import { wafExtractError } from '@/api/waf';
import { BulkSection } from './BulkSection';
import { OnOff } from './OnOff';
import { useBulkApply } from './useBulkApply';
import styles from './WafBulkConfigure.module.css';

export function MultiCdnTab({ serviceId, domains }: { serviceId: string; domains: string[] }) {
  const [compress, setCompress] = useState(false);
  const compression = useBulkApply(serviceId, 'auto-compression');

  const [ignoreQs, setIgnoreQs] = useState(false);
  const iqs = useBulkApply(serviceId, 'ignore-query-string');

  const [source, setSource] = useState('');
  const clone = useBulkApply(serviceId, 'clone-optimization');
  const domainsQ = useWafDomains(serviceId, { per_page: 200 });
  const allDomains = useMemo(() => (domainsQ.data?.items ?? []).map((d) => d.domain), [domainsQ.data]);

  return (
    <>
      <BulkSection
        title="Compress content"
        hint="Compress js, html, css, json, shtml, and htm across all CDNs."
        onApply={() => compression.mutate({ domains, enable: compress })}
        pending={compression.isPending}
      >
        <OnOff value={compress} onChange={setCompress} />
      </BulkSection>

      <BulkSection
        title="Ignore query string"
        hint="Ignore the query string of an URL when caching the website."
        onApply={() => iqs.mutate({ domains, enable: ignoreQs })}
        pending={iqs.isPending}
      >
        <OnOff value={ignoreQs} onChange={setIgnoreQs} />
      </BulkSection>

      <BulkSection
        title="Clone routing strategy"
        onApply={() => source && clone.mutate({ source, domains: domains.filter((d) => d !== source) })}
        disabled={!source}
        pending={clone.isPending}
      >
        <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>Clone from</span>
        <Select value={source} onValueChange={setSource} disabled={domainsQ.isLoading}>
          <SelectTrigger aria-label="Clone routing strategy from domain">
            <SelectValue placeholder={domainsQ.isLoading ? 'Loading…' : 'Domain'} />
          </SelectTrigger>
          <SelectContent>
            {allDomains.map((d) => (
              <SelectItem key={d} value={d}>{d}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </BulkSection>

      <CdnControlSection serviceId={serviceId} domains={domains} />
      <CustomCnameSection serviceId={serviceId} domains={domains} />
    </>
  );
}

/** CDN control — turn one custom CDN platform on/off across the selected domains. */
function CdnControlSection({ serviceId, domains }: { serviceId: string; domains: string[] }) {
  const { show } = useToast();
  const [platform, setPlatform] = useState('');
  const [enable, setEnable] = useState(false);
  // custom CDN platforms serving these domains.
  const platformsQ = useWafQueryPlatforms(serviceId, domains, { usable: true, custom: true });
  const platforms = platformsQ.data ?? [];
  const mut = useBulkSetPlatformEnabled(serviceId, {
    onSuccess: () => show({ tone: 'success', title: `Applied to ${domains.length} domain${domains.length === 1 ? '' : 's'}` }),
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Apply failed') }),
  });

  return (
    <BulkSection
      title="CDN control"
      onApply={() => platform && mut.mutate({ platform, domains, enable })}
      disabled={!platform}
      pending={mut.isPending}
    >
      <div className={styles.row}>
        <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>CDN</span>
        <Select value={platform} onValueChange={setPlatform} disabled={platformsQ.isLoading}>
          <SelectTrigger aria-label="CDN platform" style={{ minWidth: 200 }}>
            <SelectValue placeholder={platformsQ.isLoading ? 'Loading…' : 'Select a CDN'} />
          </SelectTrigger>
          <SelectContent>
            {platforms.map((p) => (
              <SelectItem key={p.name} value={p.name}>{p.display_name || p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <OnOff value={enable} onChange={setEnable} />
    </BulkSection>
  );
}

/** Custom CDN CNAME — set a CDN platform's BYO edge domain across the selected domains. */
function CustomCnameSection({ serviceId, domains }: { serviceId: string; domains: string[] }) {
  const { show } = useToast();
  const [platform, setPlatform] = useState('');
  const [cname, setCname] = useState('');
  const platformsQ = useWafQueryPlatforms(serviceId, domains, { usable: true, custom: false, with_cn2: true });
  const platforms = platformsQ.data ?? [];
  const mut = useBulkSetPlatformDomain(serviceId, {
    onSuccess: () => show({ tone: 'success', title: `Applied to ${domains.length} domain${domains.length === 1 ? '' : 's'}` }),
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Apply failed') }),
  });
  const cn = cname.trim();

  return (
    <BulkSection
      title="Custom CDN CNAME"
      onApply={() => platform && cn && mut.mutate({ platform, domains, platform_domain: cn })}
      disabled={!platform || !cn}
      pending={mut.isPending}
    >
      <div className={styles.row} style={{ gap: 'var(--vn-space-3)', alignItems: 'stretch' }}>
        <Select value={platform} onValueChange={setPlatform} disabled={platformsQ.isLoading}>
          <SelectTrigger aria-label="CDN platform" style={{ minWidth: 220 }}>
            <SelectValue placeholder={platformsQ.isLoading ? 'Loading…' : 'Select a CDN'} />
          </SelectTrigger>
          <SelectContent>
            {platforms.map((p) => (
              <SelectItem key={p.name} value={p.name}>{p.display_name || p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          placeholder="CNAME"
          value={cname}
          onChange={(e) => setCname(e.target.value)}
          style={{ flex: 1, minWidth: 220 }}
        />
      </div>
    </BulkSection>
  );
}

export default MultiCdnTab;
